import { onScopeDispose, ref } from "vue";

import {
  getScribeImportErrorMessage,
  resolveScribeImportErrorCode,
} from "./scribeImport";

/** Poll cadence while a job is starting/importing (see ER-12335 assumption). */
export const POLL_INTERVAL_MS = 2000;
/** Consecutive failed polls (network/5xx) before the dialog shows the Failed state. */
export const MAX_CONSECUTIVE_POLL_FAILURES = 3;

/**
 * Scribe import background-job orchestration (ER-12335).
 *
 * ER-12323 turned the Scribe import POST into a 202 job id plus a status endpoint,
 * so the dialog now polls and shows progress instead of blocking on one request.
 * This composable owns that lifecycle and stays free of `@/api` so it works in the
 * shared component (storybook smoke) and is unit-testable with injected functions.
 *
 * Lifecycle:
 *   entry --start(url)--> starting --poll(running+progress.total)--> importing
 *   importing --poll(succeeded)--> onSucceeded(result)
 *   any --poll(failed)--> failed           any --retry()--> start(lastUrl)
 *   any --cancel()--> stops polling        any --reset()--> entry
 *
 * ER-12394 reuses the same lifecycle for the PDF import by injecting the start/poll
 * functions, the start payload builder and the 422 error mapper; the defaults keep the
 * Scribe behavior unchanged.
 *
 * @param {object} options
 * @param {(payload: *) => Promise} options.startImport  POST 202 -> { data: { job_id } };
 *                                                       422 rejects with an import error code.
 * @param {(jobId: string) => Promise} options.pollStatus            GET -> { data: { status, progress } }.
 * @param {(result: object) => void} [options.onSucceeded]           called with the succeeded payload.
 * @param {(input: *) => *} [options.buildPayload]       maps what `start()` receives to the `startImport`
 *                                                       argument (default: the Scribe `{ url }` payload).
 * @param {(error: *) => string} [options.resolveErrorMessage]  maps a rejected start to the entry-state
 *                                                       message (default: the Scribe code messages).
 */
export function useScribeImportJob({
  startImport,
  pollStatus,
  onSucceeded,
  buildPayload = (url) => ({ url }),
  resolveErrorMessage = (err) => getScribeImportErrorMessage(resolveScribeImportErrorCode(err)),
} = {}) {
  /** entry | starting | importing | failed */
  const phase = ref("entry");
  const progress = ref({ done: 0, total: 0 });
  const entryError = ref("");
  /** The status payload of the last job that ended `failed` (null otherwise). */
  const failureData = ref(null);
  /** True only while the POST itself is in flight (entry -> starting). */
  const isStarting = ref(false);

  let pollTimer = null;
  let active = false;
  let consecutiveFailures = 0;
  let lastInput = "";
  /** Bumped by start/cancel/reset so results of superseded requests are ignored. */
  let runId = 0;

  function stopPolling() {
    active = false;
    if (pollTimer !== null) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  }

  function toFailed(data = null) {
    stopPolling();
    failureData.value = data;
    phase.value = "failed";
    progress.value = { done: 0, total: 0 };
  }

  function beginPolling(jobId) {
    stopPolling();
    active = true;
    consecutiveFailures = 0;
    const run = runId;
    pollTimer = setInterval(() => poll(jobId, run), POLL_INTERVAL_MS);
  }

  async function start(input) {
    if (typeof startImport !== "function") {
      return;
    }
    entryError.value = "";
    failureData.value = null;
    isStarting.value = true;
    lastInput = input;
    const run = ++runId;
    try {
      const res = await startImport(buildPayload(input));
      if (run !== runId) {
        return;
      }
      isStarting.value = false;
      const data = res?.data ?? {};
      if (data.job_id) {
        phase.value = "starting";
        progress.value = { done: 0, total: 0 };
        beginPolling(data.job_id);
        return;
      }
      // Defensive fallback: a payload with no job id is treated as a direct result.
      if (typeof onSucceeded === "function") {
        onSucceeded(data);
      }
    } catch (err) {
      if (run !== runId) {
        return;
      }
      isStarting.value = false;
      entryError.value = resolveErrorMessage(err);
    }
  }

  async function poll(jobId, run) {
    if (!active || typeof pollStatus !== "function") {
      return;
    }
    try {
      const res = await pollStatus(jobId);
      if (run !== runId) {
        return;
      }
      consecutiveFailures = 0;
      const data = res?.data ?? {};
      if (data.status === "succeeded") {
        stopPolling();
        if (typeof onSucceeded === "function") {
          onSucceeded(data.result);
        }
        return;
      }
      if (data.status === "failed") {
        toFailed(data);
        return;
      }
      // pending / no progress.total -> keep the empty Starting bar.
      const total = data.progress?.total;
      if (data.status === "running" && total) {
        progress.value = { done: data.progress?.done ?? 0, total };
        phase.value = "importing";
      } else {
        phase.value = "starting";
      }
    } catch {
      if (run !== runId) {
        return;
      }
      consecutiveFailures += 1;
      if (consecutiveFailures >= MAX_CONSECUTIVE_POLL_FAILURES) {
        toFailed();
      }
    }
  }

  function retry() {
    if (lastInput) {
      start(lastInput);
    }
  }

  function cancel() {
    runId += 1;
    isStarting.value = false;
    stopPolling();
  }

  function reset() {
    runId += 1;
    stopPolling();
    phase.value = "entry";
    progress.value = { done: 0, total: 0 };
    entryError.value = "";
    failureData.value = null;
    isStarting.value = false;
    consecutiveFailures = 0;
    lastInput = "";
  }

  onScopeDispose(stopPolling);

  return { phase, progress, entryError, failureData, isStarting, start, retry, cancel, reset };
}
